/**
 * Os itens da lista do Desk Cloud (docs/spec/desk-cloud.md): o cartão da grade, a linha da lista e o
 * bloco de pasta. Clicar foca (abre os detalhes); Ctrl/Cmd+clique e a caixa põem na seleção; Shift
 * pega o intervalo; dois cliques abrem. O botão direito, o ⋯ e segurar o dedo abrem o menu (no celular,
 * segurar entra no modo seleção). Os arquivos e as pastas se arrastam para pastas, mesas e a lixeira.
 */
import { useState, type DragEvent, type MouseEvent as RMouseEvent } from 'react';
import { formatBytes } from '../bytes.js';
import { useLanguage, useT } from '../i18n/index.js';
import type { DeskFile, DeskFolder } from '../shared.js';
import { Icon } from '../ui/icons.js';
import { useLongPress } from '../ui/touch.js';
import { moveFiles, moveFolder, favoriteFiles } from './cloudActions.js';
import { KIND_COLORS, dateText, durationText, plural, roomColor, roomInitials, splitName } from './cloudModel.js';
import { useCloud } from './cloudStore.js';
import { FOLDER_DRAG, ITEMS_DRAG, useCloudUi } from './cloudUi.js';
import { setFileDrag } from './fileDrag.js';
import { FileThumb } from './FileThumb.js';

/** O modo do clique pelas teclas: Shift pega o intervalo, Ctrl/Cmd põe e tira. */
const clickMode = (e: RMouseEvent): 'single' | 'toggle' | 'range' => (e.shiftKey ? 'range' : e.ctrlKey || e.metaKey ? 'toggle' : 'single');

/** O clique num arquivo: sem tecla foca (os detalhes) e solta a seleção; com Ctrl/Shift, mexe na seleção. */
export function clickFile(file: DeskFile, e: RMouseEvent, mobile: boolean): void {
  const cloud = useCloud.getState();
  const mode = clickMode(e);
  // no celular, com a seleção aberta, tocar põe e tira
  if (mobile && cloud.selected.size) {
    cloud.select(file.id, 'toggle');
    return;
  }
  if (mode === 'single') {
    cloud.clearSelection();
    cloud.setFocus(file.id);
    useCloud.setState({ anchor: file.id });
    return;
  }
  // o clique simples já escolheu um (o focado): Ctrl e Shift partem dele, como nos gerenciadores de arquivos
  if (!cloud.selected.size && cloud.focus && cloud.focus !== file.id) cloud.select(cloud.focus, 'toggle');
  cloud.select(file.id, mode);
}

/** Começa o arraste de um arquivo: leva a seleção, se ele está nela, ou só ele. */
function startDrag(e: DragEvent, file: DeskFile): void {
  const { selected } = useCloud.getState();
  const ids = selected.has(file.id) ? [...selected] : [file.id];
  e.dataTransfer.setData(ITEMS_DRAG, JSON.stringify(ids));
  e.dataTransfer.setData('text/plain', file.name);
  if (ids.length === 1) setFileDrag(e.dataTransfer, file);
  e.dataTransfer.effectAllowed = 'copyMove';
}

/** As mesas que podem abrir o arquivo: as iniciais e "Só você", o nome da mesa ou "N mesas". */
export function Access({ file }: { file: DeskFile }) {
  const t = useT();
  const ui = useCloudUi();
  const names = file.rooms.map((id) => ui.roomName(id));
  const text = !file.rooms.length ? t('cloud.access.mine') : file.rooms.length === 1 ? names[0]! : t('cloud.access.rooms', { n: file.rooms.length });
  return (
    <span className={`rd-cloudAccess${file.rooms.length ? '' : ' is-mine'}`} title={file.rooms.length ? t('cloud.access.title', { rooms: names.join(', ') }) : t('cloud.access.titleMine')}>
      {file.rooms.length ? (
        <span className="rd-cloudAccess__chips" aria-hidden="true">
          {file.rooms.slice(0, 3).map((id, i) => (
            <span key={id} className="rd-cloudAccess__chip" style={{ background: roomColor(id) }}>
              {roomInitials(names[i]!)}
            </span>
          ))}
        </span>
      ) : null}
      <span className="rd-cloudAccess__text">{text}</span>
    </span>
  );
}

function Star({ file, compact = false }: { file: DeskFile; compact?: boolean }) {
  const t = useT();
  if (file.trashedAt !== null) return null;
  return (
    <button
      type="button"
      className={`rd-cloudStar${file.favorite ? ' is-on' : ''}${compact ? ' is-compact' : ''}`}
      aria-label={t(file.favorite ? 'cloud.fav.remove' : 'cloud.fav.add')}
      aria-pressed={file.favorite}
      title={t(file.favorite ? 'cloud.fav.remove' : 'cloud.fav.add')}
      onClick={(e) => {
        e.stopPropagation();
        void favoriteFiles([file], !file.favorite);
      }}
    >
      <Icon name="star" size={16} filled={file.favorite} />
    </button>
  );
}

function More({ file }: { file: DeskFile }) {
  const t = useT();
  const ui = useCloudUi();
  return (
    <button
      type="button"
      className="rd-iconBtn rd-cloudMore"
      aria-label={t('cloud.more', { name: file.name })}
      onClick={(e) => {
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        ui.menuForFile(file, { x: r.left, y: r.bottom });
      }}
    >
      ⋯
    </button>
  );
}

/** O que segurar faz: o menu no computador; no celular, entra na seleção (o ⋯ abre o menu). */
function usePress(file: DeskFile) {
  const ui = useCloudUi();
  const press = useLongPress();
  return press.bind((anchor) => {
    if (ui.mobile) useCloud.getState().select(file.id, 'toggle');
    else ui.menuForFile(file, anchor);
  });
}

export function FileCard({ file, selected, focused }: { file: DeskFile; selected: boolean; focused: boolean }) {
  const t = useT();
  const ui = useCloudUi();
  const language = useLanguage((s) => s.language);
  const press = usePress(file);
  const { stem, ext } = splitName(file.name);
  return (
    <div className={`rd-cloudCard${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`} draggable={!ui.mobile} onDragStart={(e) => startDrag(e, file)} {...press}>
      <button
        type="button"
        className="rd-cloudCard__hit"
        aria-label={`${file.name}, ${t(`cloud.kind.${file.kind}`)}, ${formatBytes(file.size)}`}
        aria-pressed={focused}
        data-file={file.id}
        onClick={(e) => clickFile(file, e, ui.mobile)}
        onDoubleClick={() => ui.openFile(file)}
      />
      <div className="rd-cloudCard__media">
        <FileThumb file={file} size="card" />
        <span className="rd-cloudCard__kind">
          <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
          {t(`cloud.kind.${file.kind}`)}
        </span>
        <input className="rd-cloudCard__check" type="checkbox" checked={selected} aria-label={t('cloud.select', { name: file.name })} onChange={() => useCloud.getState().select(file.id, 'toggle')} onClick={(e) => e.stopPropagation()} />
        <Star file={file} />
      </div>
      <div className="rd-cloudCard__info">
        <div className="rd-cloudCard__name" title={file.name}>
          {stem}
          {ext ? <span className="rd-cloudCard__ext">.{ext}</span> : null}
        </div>
        <div className="rd-cloudCard__meta">
          <span>
            {formatBytes(file.size)} · {dateText(t, file.createdAt, language)}
          </span>
        </div>
        {/* numa linha só dela: ao lado do tamanho e da data, o nome da mesa não cabia e sumia */}
        {file.trashedAt === null ? (
          <div className="rd-cloudCard__access">
            <Access file={file} />
          </div>
        ) : null}
      </div>
      <More file={file} />
    </div>
  );
}

export function FileRow({ file, selected, focused, compact, selecting }: { file: DeskFile; selected: boolean; focused: boolean; compact: boolean; selecting: boolean }) {
  const t = useT();
  const ui = useCloudUi();
  const language = useLanguage((s) => s.language);
  const press = usePress(file);
  const { stem, ext } = splitName(file.name);
  const date = dateText(t, file.createdAt, language);
  if (compact)
    return (
      <div className={`rd-cloudRow is-compact${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`} role="row" {...press}>
        {selecting ? (
          <span role="cell" className="rd-cloudRow__check">
            <input type="checkbox" checked={selected} aria-label={t('cloud.select', { name: file.name })} onChange={() => useCloud.getState().select(file.id, 'toggle')} />
          </span>
        ) : null}
        <button type="button" role="cell" className="rd-cloudRow__main" data-file={file.id} onClick={(e) => clickFile(file, e, ui.mobile)} onDoubleClick={() => ui.openFile(file)}>
          <FileThumb file={file} size="row" />
          <span className="rd-cloudRow__text">
            <span className="rd-cloudRow__name">
              {stem}
              {ext ? <span className="rd-cloudCard__ext">.{ext}</span> : null}
            </span>
            <span className="rd-cloudRow__meta">
              <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
              {t(`cloud.kind.${file.kind}`)} · {file.kind === 'audio' && file.durationMs !== null ? durationText(file.durationMs) : formatBytes(file.size)} · {date}
            </span>
          </span>
        </button>
        <span role="cell" className="rd-cloudRow__end">
          <More file={file} />
        </span>
      </div>
    );
  return (
    <div className={`rd-cloudRow${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`} role="row" draggable onDragStart={(e) => startDrag(e, file)} {...press}>
      <span role="cell" className="rd-cloudRow__check">
        <input type="checkbox" checked={selected} aria-label={t('cloud.select', { name: file.name })} onChange={() => useCloud.getState().select(file.id, 'toggle')} />
      </span>
      <button type="button" role="cell" className="rd-cloudRow__main" aria-pressed={focused} data-file={file.id} onClick={(e) => clickFile(file, e, false)} onDoubleClick={() => ui.openFile(file)}>
        <FileThumb file={file} size="row" />
        <span className="rd-cloudRow__name">
          {stem}
          {ext ? <span className="rd-cloudCard__ext">.{ext}</span> : null}
        </span>
        {file.favorite ? <Icon name="star" size={13} filled /> : null}
      </button>
      <span role="cell" className="rd-cloudRow__kind">
        <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
        {t(`cloud.kind.${file.kind}`)}
      </span>
      <span role="cell" className="rd-cloudRow__access">
        {file.trashedAt === null ? <Access file={file} /> : null}
      </span>
      <span role="cell" className="rd-cloudRow__size">
        {formatBytes(file.size)}
      </span>
      <span role="cell" className="rd-cloudRow__date">
        {date}
      </span>
      <span role="cell" className="rd-cloudRow__end">
        <Star file={file} compact />
        <More file={file} />
      </span>
    </div>
  );
}

/** O bloco de uma pasta no topo da lista: abre, recebe arquivos e pastas arrastados, e tem o menu dela. */
export function FolderTile({ folder }: { folder: DeskFolder }) {
  const ui = useCloudUi();
  const press = useLongPress();
  const [over, setOver] = useState(false);
  const folders = useCloud((s) => s.summary?.folders) ?? [];
  const subfolders = folders.filter((f) => f.parentId === folder.id).length;
  return (
    <button
      type="button"
      className={`rd-cloudFolder${over ? ' is-over' : ''}`}
      draggable={!ui.mobile}
      onDragStart={(e) => {
        e.dataTransfer.setData(FOLDER_DRAG, folder.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(ITEMS_DRAG) && !e.dataTransfer.types.includes(FOLDER_DRAG)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const items = e.dataTransfer.getData(ITEMS_DRAG);
        const moving = e.dataTransfer.getData(FOLDER_DRAG);
        if (!items && !moving) return;
        e.preventDefault();
        e.stopPropagation();
        if (items) void moveFiles(useCloud.getState().filesOf(JSON.parse(items) as string[]), folder.id);
        else {
          const other = folders.find((f) => f.id === moving);
          if (other && other.id !== folder.id) void moveFolder(other, folder.id);
        }
      }}
      onClick={() => ui.folderActions.openFolder(folder)}
      {...press.bind((anchor) => ui.menuForFolder(folder, anchor))}
    >
      <FolderFace folder={folder} subfolders={subfolders} />
    </button>
  );
}

/** O ícone, o nome e a linha de baixo do bloco de pasta (a página e o seletor de arquivos). */
export function FolderFace({ folder, subfolders }: { folder: DeskFolder; subfolders: number }) {
  const t = useT();
  return (
    <>
      <span className="rd-cloudFolder__icon" aria-hidden="true">
        <Icon name="folder" size={20} />
      </span>
      <span className="rd-cloudFolder__text">
        <span className="rd-cloudFolder__name">{folder.name}</span>
        <span className="rd-cloudFolder__meta">
          {/* as pastas de dentro também contam: uma pasta só com subpastas não parece vazia */}
          {subfolders ? `${plural(t, 'cloud.subfolders', subfolders)} · ` : ''}
          {t('cloud.folderTile', { files: plural(t, 'cloud.files', folder.files), size: formatBytes(folder.bytes) })}
        </span>
      </span>
    </>
  );
}
